import { t } from '@lingui/core/macro'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import type { DocumentPageMetadataUserEditEntry } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { buildAppendPageMetadataUserEditCommentPatch } from 'features/knowledgeDocumentViewer/utils/buildDocumentPageMetadataUserEditCommentPatch'
import { useInvalidateDocumentPageMetadataUserEditLoads } from 'features/knowledgeDocumentViewer/utils/invalidateDocumentPageMetadataUserEditLoads'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useState } from 'react'

export type AppendDocumentPageMetadataUserEditCommentArgs = {
  host: DocumentPageMetadataUserEditEntry | undefined
  text: string
  bqDocumentId: string | undefined
}

export function useAppendDocumentPageMetadataUserEditComment(): {
  isAppending: boolean
  appendComment: (
    args: AppendDocumentPageMetadataUserEditCommentArgs
  ) => Promise<boolean>
} {
  const { rolodexPatch } = useRolodexEdit()
  const { user } = useGeckoPermissions()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const invalidateDocumentPageMetadataUserEditLoads =
    useInvalidateDocumentPageMetadataUserEditLoads()
  const [isAppending, setIsAppending] = useState(false)

  const appendComment = useCallback(
    async (args: AppendDocumentPageMetadataUserEditCommentArgs) => {
      const patch = buildAppendPageMetadataUserEditCommentPatch({
        host: args.host,
        text: args.text,
        createdBy: user?.email ?? '',
        createdAt: new Date().toISOString(),
      })
      if (patch === undefined) {
        if (args.host === undefined) {
          errorNotification(
            t`Confirm or defer this page before adding a comment`
          )
        }
        return false
      }
      if (!args.bqDocumentId) {
        errorNotification(t`Cannot save a comment without a document id`)
        return false
      }

      setIsAppending(true)
      try {
        await rolodexPatch([patch])
        void invalidateDocumentPageMetadataUserEditLoads().catch(() =>
          errorNotification(
            t`Comment added, but updated comments could not be refreshed`
          )
        )
        successNotification(t`Comment added`, {
          duration: 2,
          placement: 'topRight',
        })
        return true
      } catch {
        errorNotification(t`Failed to add comment`)
        return false
      } finally {
        setIsAppending(false)
      }
    },
    [
      errorNotification,
      invalidateDocumentPageMetadataUserEditLoads,
      rolodexPatch,
      successNotification,
      user?.email,
    ]
  )

  return { isAppending, appendComment }
}
